/**
 * Incoming Call Toast — top-right notification for incoming calls.
 * Shows caller info with Accept, Decline, and Ignore buttons.
 * Auto-dismisses after 30 seconds.
 */

import { useState, useEffect, useCallback } from 'react';
import { Phone, PhoneOff, X, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCallStore } from '@/stores/call-store';

const AUTO_DISMISS_MS = 30000;

export function IncomingCallToast() {
  const activeCalls = useCallStore((s) => s.activeCalls);
  const acceptCall = useCallStore((s) => s.acceptCall);
  const endCall = useCallStore((s) => s.endCall);
  const ignoreCall = useCallStore((s) => s.ignoreCall);

  // Find the first incoming call that's ringing
  const incomingCall = activeCalls.find(
    (c) => c.direction === 'inbound' && c.status === 'ringing'
  );

  const [visible, setVisible] = useState(false);
  const [callId, setCallId] = useState<string | null>(null);

  // Show/hide based on incoming call
  useEffect(() => {
    if (incomingCall) {
      setCallId(incomingCall.id);
      setVisible(true);
    } else {
      setVisible(false);
      // Clear callId after animation
      const timer = setTimeout(() => setCallId(null), 300);
      return () => clearTimeout(timer);
    }
  }, [incomingCall]);

  // Auto-dismiss after timeout
  useEffect(() => {
    if (!incomingCall) return;
    const timer = setTimeout(() => {
      ignoreCall(incomingCall.id);
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [incomingCall, ignoreCall]);

  const handleAccept = useCallback(() => {
    if (callId) {
      acceptCall(callId);
    }
  }, [callId, acceptCall]);

  const handleDecline = useCallback(() => {
    if (callId) {
      endCall(callId);
    }
  }, [callId, endCall]);

  const handleIgnore = useCallback(() => {
    if (callId) {
      ignoreCall(callId);
    }
  }, [callId, ignoreCall]);

  if (!callId) return null;

  const isWhatsAppCall = incomingCall?.callChannel === 'whatsapp';
  const displayNumber = (incomingCall?.customerNumber || 'Unknown').replace('whatsapp:', '');

  return (
    <div
      className={cn(
        'fixed top-4 right-4 z-[60] w-80 transition-all duration-300 ease-out',
        visible
          ? 'translate-y-0 opacity-100 scale-100'
          : '-translate-y-4 opacity-0 scale-95 pointer-events-none'
      )}
    >
      <div className="rounded-xl border border-border bg-background/95 backdrop-blur-sm shadow-2xl overflow-hidden">
        {/* Header with pulse ring */}
        <div className="flex items-center gap-3 px-4 pt-4 pb-3">
          <div className="relative">
            <div className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full',
              isWhatsAppCall ? 'bg-green-500/15' : 'bg-emerald-500/15'
            )}>
              {isWhatsAppCall ? (
                <MessageCircle className="h-5 w-5 text-green-500" />
              ) : (
                <Phone className="h-5 w-5 text-emerald-500" />
              )}
            </div>
            {/* Pulse animation */}
            <div className={cn(
              'absolute inset-0 animate-ping rounded-full',
              isWhatsAppCall ? 'bg-green-500/20' : 'bg-emerald-500/20'
            )} />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-muted-foreground">
              {isWhatsAppCall ? 'WhatsApp Call' : 'Incoming Call'}
            </p>
            <p className="text-sm font-semibold text-foreground truncate">
              {displayNumber}
            </p>
          </div>

          <button
            onClick={handleIgnore}
            className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Ignore"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 px-4 pb-4">
          <button
            onClick={handleDecline}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-destructive/10 px-3 py-2.5 text-sm font-medium text-destructive hover:bg-destructive/20 transition-colors"
          >
            <PhoneOff className="h-4 w-4" />
            Decline
          </button>
          <button
            onClick={handleAccept}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-emerald-500 px-3 py-2.5 text-sm font-medium text-white hover:bg-emerald-600 transition-colors"
          >
            <Phone className="h-4 w-4" />
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
